import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { LayoutGrid, List, Filter, Plus, Briefcase, Wrench, Kanban, KanbanSquare, Columns3 } from 'lucide-react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

import axios from 'helpers/axios';
import config from 'helpers/config';
import history from 'helpers/history';
import actionTypes from 'helpers/actionTypes';

import RepairOrder from 'models/RepairOrderModel';
import User from 'models/UserModel';
import WorkflowStatus from 'models/WorkflowStatusModel';

import Overlay from 'components/common/Overlay';
import BoardColumn from './BoardColumn';
import JobListView from '../jobListView/JobListView';
import LaborRate from 'models/LaborRateModel';
import TagsPicker from 'components/common/TagsPicker';
import Label from 'models/LabelModel';
import StartingRONumberModal from './StartingRONumberModal';
import * as RepairOrderHelper from 'helpers/repairOrderHelper';
import { LongRunningRoHighlightStyle } from 'models/TenantSettingsModel';
import Tenant from 'models/TenantModel';

interface ComponentState {
    isNewRepairOrderLoading: boolean;
    repairOrders: RepairOrder[];
    filteredRepairOrders: RepairOrder[];
    isBoardViewActive: boolean;
    workflowStatuses: WorkflowStatus[];
    laborRates: LaborRate[];
    isLoading: boolean;
    isFiltersExpanded: boolean;
    isStartingNumberModalOpen: boolean;

    employees: User[];
}

interface Props {
    dispatch: Dispatch;
    currentUser: User;
    tenant: Tenant;
}

class JobListPage extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isNewRepairOrderLoading: false,
            isBoardViewActive: true,
            repairOrders: [],
            filteredRepairOrders: [],
            workflowStatuses: [],
            laborRates: [],
            isLoading: true,
            employees: [],
            isFiltersExpanded: false,
            isStartingNumberModalOpen: false,
        };

        this.reorder = this.reorder.bind(this);
        this.handleJobArchivedOrDeleted = this.handleJobArchivedOrDeleted.bind(this);
    }

    componentDidMount() {
        this.getActiveRepairOrders();
        this.getWorkflowStatuses();
        this.getLaborRates();
        this.getEmployees();
    }

    getEmployees() {
        axios.get(`${config.apiUrl}/user`).then((res) => {
            this.setState({ employees: res.data });
        });
    }

    getLaborRates() {
        axios.get(`${config.apiUrl}/laborRate`).then((res) => {
            this.setState({ laborRates: res.data });
        });
    }

    getActiveRepairOrders() {
        axios
            .get(`${config.apiUrl}/repairOrder/board`)
            .then((res) => this.setState({ repairOrders: res.data }))
            .catch((_) => console.log(_))
            .finally(() => this.setState({ isLoading: false }));
    }

    getWorkflowStatuses() {
        axios
            .get(`${config.apiUrl}/tenant/workflowStatuses`)
            .then((res) => this.setState({ workflowStatuses: res.data }))
            .catch((err) => console.log(err));
    }

    async goToRepairOrder() {
        this.setState({ isNewRepairOrderLoading: true });

        await RepairOrderHelper.createAndNavigateToRepairOrder({}, () => {
            this.setState({ isStartingNumberModalOpen: true });
        });

        this.setState({ isNewRepairOrderLoading: false });
    }

    handleStartingNumberConfirm = async (startingNumber: number) => {
        try {
            await RepairOrderHelper.updateStartingRONumber(startingNumber);
            this.setState({ isStartingNumberModalOpen: false, isNewRepairOrderLoading: true });

            const roId = await RepairOrderHelper.createRepairOrder({});
            if (roId) {
                history.push(`/repairOrder/${roId}`);
            }
        } catch (error) {
            console.error('Error creating repair order:', error);
        } finally {
            this.setState({ isNewRepairOrderLoading: false });
        }
    };

    reorder(dropEvent: DropResult) {
        if (!!!dropEvent.destination) {
            return;
        }

        const { destination, source, draggableId } = dropEvent;
        // Not actually moved anywhere
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const destinationTasks = this.state.repairOrders
            .filter((RO) => RO.workflowStatusId === destination.droppableId)
            .sort((RO1: RepairOrder, RO2: RepairOrder) => {
                return RO1.columnOrderIndex - RO2.columnOrderIndex;
            });

        let newColumnOrderIndex;

        // dropped into empty column
        if (destinationTasks.length === 0) {
            newColumnOrderIndex = 1000;
        } else if (destinationTasks.length === 1) {
            // dropped into column with one task only
            newColumnOrderIndex =
                destination.index === 0
                    ? destinationTasks[0].columnOrderIndex / 2
                    : destinationTasks[0].columnOrderIndex + 1000;
        } else {
            // within the same column
            if (destination.droppableId === source.droppableId) {
                newColumnOrderIndex =
                    destination.index < source.index
                        ? destination.index !== 0
                            ? (destinationTasks[destination.index - 1].columnOrderIndex +
                                  destinationTasks[destination.index].columnOrderIndex) /
                              2
                            : destinationTasks[0].columnOrderIndex / 2
                        : destination.index !== destinationTasks.length - 1
                        ? (destinationTasks[destination.index + 1].columnOrderIndex +
                              destinationTasks[destination.index].columnOrderIndex) /
                          2
                        : destinationTasks[destinationTasks.length - 1].columnOrderIndex + 100;
            } else {
                // dropped into another column
                if (destination.index === 0) {
                    newColumnOrderIndex = destinationTasks[0].columnOrderIndex / 2;
                } else if (destination.index === destinationTasks.length) {
                    newColumnOrderIndex = destinationTasks[destination.index - 1].columnOrderIndex + 1000;
                } else {
                    newColumnOrderIndex =
                        (destinationTasks[destination.index - 1].columnOrderIndex +
                            destinationTasks[destination.index].columnOrderIndex) /
                        2;
                }
            }
        }

        const { repairOrders } = this.state;
        const ticketIndex = this.state.repairOrders.findIndex((RO) => RO.id === draggableId);
        repairOrders[ticketIndex].workflowStatusId = dropEvent.destination.droppableId;
        repairOrders[ticketIndex].columnOrderIndex = newColumnOrderIndex;

        this.setState(
            {
                repairOrders,
            },
            () => {
                this.updateWorkflowStatus(this.state.repairOrders[ticketIndex]);
            },
        );

        setTimeout(() => {
            const cardRef = document.querySelector(`#ticket${draggableId}`) as HTMLDivElement;
            if (cardRef) {
                if (
                    this.props.tenant?.tenantSettings?.isLongRunningRoHighlightEnabled &&
                    this.props.tenant?.tenantSettings?.longRunningRoHighlightStyle !==
                        LongRunningRoHighlightStyle.BACKGROUND_TINT
                ) {
                    cardRef.style.borderColor = this.props.tenant?.tenantSettings?.longRunningRoHighlightColor;
                }
            }
        }, 50);
    }

    updateWorkflowStatus(repairOrder: RepairOrder) {
        axios
            .put(`${config.apiUrl}/repairOrder?withoutSuccessMessage=true`, repairOrder)
            .then((_) => _)
            .catch((err) => console.log(err));
    }

    handleJobArchivedOrDeleted(jobId: string) {
        const activeRepairOrders = this.state.repairOrders.filter((RO) => RO.id !== jobId);
        this.setState({ repairOrders: activeRepairOrders });
    }

    isBoardViewActive() {
        if (!!this.props?.currentUser?.userSetting) {
            return this.props.currentUser.userSetting.isBoardViewActive;
        } else {
            return true;
        }
    }

    updateListViewSettings() {
        const { userSetting: userSettings } = this.props.currentUser;
        userSettings.isBoardViewActive = !userSettings.isBoardViewActive;
        axios
            .put(`${config.apiUrl}/user/settings`, userSettings)
            .then(() => this.props.dispatch({ type: actionTypes.UPDATE_USER_SETTINGS_SUCCESS, payload: userSettings }))
            .catch((_) => console.log(_));
    }

    filterByTags(tags: Label[]) {
        const { repairOrders } = this.state;
        if (tags.length > 0) {
            const filtered = repairOrders.filter((RO) => {
                return RO.labels.some((label) => tags.map((tag) => tag.id).includes(label.id));
            });
            this.setState({ filteredRepairOrders: filtered });
        } else {
            this.setState({ filteredRepairOrders: repairOrders });
        }
    }
    render() {
        const {
            isNewRepairOrderLoading,
            repairOrders,
            workflowStatuses,
            laborRates,
            isLoading,
            filteredRepairOrders,
            isStartingNumberModalOpen,
        } = this.state;
        return (
            <div className="min-h-screen bg-gray-50">
                {isLoading && <Overlay />}

                <StartingRONumberModal
                    isOpen={isStartingNumberModalOpen}
                    onClose={() => this.setState({ isStartingNumberModalOpen: false })}
                    onConfirm={this.handleStartingNumberConfirm}
                />

                {/* Header */}
                <div className="bg-white border-b border-gray-200">
                    <div className="px-6 py-4">
                        {/* Title and New Repair Order Button */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <Wrench className="h-8 w-8 text-blue-600" />
                                <h1 className="text-3xl font-bold text-gray-900">Job Centre</h1>
                            </div>
                            <button
                                disabled={isNewRepairOrderLoading}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                onClick={() => this.goToRepairOrder()}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                New Repair Order
                            </button>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center justify-between">
                            {/* View Toggle */}
                            <div className="flex items-center space-x-4">
                                <div className="flex bg-gray-100 rounded-lg p-1">
                                    <button
                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            this.isBoardViewActive()
                                                ? 'bg-white text-blue-600  shadow-sm'
                                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                        onClick={() => {
                                            if (!this.isBoardViewActive()) {
                                                this.updateListViewSettings();
                                            }
                                        }}
                                        title="Board View"
                                    >
                                        <Columns3 className="h-4 w-4 mr-2" />
                                        Board
                                    </button>
                                    <button
                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            !this.isBoardViewActive()
                                                ? 'bg-white text-blue-600 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                        onClick={() => {
                                            if (this.isBoardViewActive()) {
                                                this.updateListViewSettings();
                                            }
                                        }}
                                        title="List View"
                                    >
                                        <List className="h-4 w-4 mr-2" />
                                        List
                                    </button>
                                </div>
                            </div>

                            {/* Filters */}
                            <div className="flex items-center space-x-4">
                                {/* <div className="flex items-center space-x-2 text-sm text-gray-700">
                                    <Filter className="h-4 w-4" />
                                    <span>Filters:</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <TagsPicker
                                        onchange={(tags: Label[]) => {
                                            this.filterByTags(tags);
                                        }}
                                    />
                                </div> */}

                                <button
                                    className="flex items-center text-black space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                        this.setState({ isFiltersExpanded: !this.state.isFiltersExpanded });
                                    }}
                                >
                                    <Filter className="h-4 w-4" />
                                    <span className="text-sm">Filters</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                {/* hide it with css class when !isFiltersExpanded */}
                <div
                    className={`p-4 bg-gray-50 border-b border-gray-200 ${
                        !this.state.isFiltersExpanded ? 'hidden' : ''
                    }`}
                >
                    <div className="flex gap-4 items-center">
                        <TagsPicker
                            onchange={(tags: Label[]) => {
                                this.filterByTags(tags);
                            }}
                        />
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 p-5">
                    {this.isBoardViewActive() ? (
                        <DragDropContext onDragEnd={this.reorder}>
                            <div className="inline-flex w-full h-full">
                                {workflowStatuses.map((workflowStatus: WorkflowStatus) => {
                                    return (
                                        <div
                                            key={workflowStatus.id}
                                            className={`w-1/${workflowStatuses?.length} min-h-screen`}
                                        >
                                            <BoardColumn
                                                column={workflowStatus}
                                                tasks={
                                                    filteredRepairOrders.length
                                                        ? filteredRepairOrders.filter(
                                                              (RO) => RO.workflowStatusId === workflowStatus.id,
                                                          )
                                                        : repairOrders.filter(
                                                              (RO) => RO.workflowStatusId === workflowStatus.id,
                                                          )
                                                }
                                                jobArchivedOrDeleted={this.handleJobArchivedOrDeleted}
                                                laborRates={laborRates}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </DragDropContext>
                    ) : (
                        <div>
                            {this.state.workflowStatuses?.length > 0 && !!repairOrders && (
                                <JobListView
                                    repairOrders={filteredRepairOrders.length > 0 ? filteredRepairOrders : repairOrders}
                                    workflowStatuses={this.state.workflowStatuses}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
    tenant: state.tenant.tenant,
});

export default connect(mapStateToProps)(JobListPage);
